/**
 * Client booking access without registration.
 *
 * The token is derived on the server: HMAC-SHA256(BOOKING_TOKEN_SECRET, tenantId:idempotencyKey).
 * Only sha256(token) is stored in the database. A retry of the same request
 * (same idempotency key) therefore re-derives exactly the same token, so a
 * client that lost the first response still gets access to its booking —
 * without the database ever holding the raw token.
 * Uses WebCrypto only (works in Deno, browsers and Node >= 20).
 */
const enc = new TextEncoder();

function toHex(buf: ArrayBuffer): string {
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

function toBase64Url(buf: ArrayBuffer): string {
  let s = '';
  for (const b of new Uint8Array(buf)) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export async function deriveAccessToken(secret: string, tenantId: string, idempotencyKey: string): Promise<string> {
  if (secret.length < 32) throw new Error('BOOKING_TOKEN_SECRET must be at least 32 characters');
  const key = await crypto.subtle.importKey('raw', enc.encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
  const sig = await crypto.subtle.sign('HMAC', key, enc.encode(`${tenantId}:${idempotencyKey}`));
  return toBase64Url(sig);
}

export async function sha256Hex(input: string): Promise<string> {
  return toHex(await crypto.subtle.digest('SHA-256', enc.encode(input)));
}

export const hashToken = sha256Hex;

/** Canonical hash of the booking request, used to detect idempotency-key reuse with a different payload. */
export async function requestHash(fields: Record<string, string | number | null | undefined>): Promise<string> {
  const keys = Object.keys(fields).sort();
  return sha256Hex(JSON.stringify(keys.map((k) => [k, fields[k] ?? null])));
}

export const TOKEN_RE = /^[A-Za-z0-9_-]{43}$/;
export const IDEMPOTENCY_RE = /^[A-Za-z0-9_-]{16,100}$/;
